import { t } from 'i18next';
import { trackPromise } from 'react-promise-tracker';

import _ from 'lodash';

import axiosInstance from '../axios';
import { parseError, getTranslationValueFromKey } from '../utils/helpers';
import logger from '../utils/debugHelpers';

import { PATIENT, TELEEYE_MD_WEB } from '../constants/constants';
import {
  GET_PATIENTS,
  GET_PATIENT_DETAILS,
  CREATE_PATIENT,
  GET_PATIENT_EXAMS,
  UPDATE_PATIENT_CONTACT_DETAILS,
  UPDATE_PATIENT_DEMOGRAPHIC_DETAILS,
  SET_SNACKBAR_SUCCESS,
  SET_SNACKBAR_ERROR,
  GET_PATIENT_RISK_SERVICES,
  SET_SNACKBAR_INFO,
  GET_PATIENT_AUDIO_RECORDS,
} from './types';

// GET ALL PATIENTS IN ORGANISATION
export const getPatients =
  (pageSize = 7, page = 1) =>
  (dispatch) => {
    return trackPromise(
      axiosInstance
        .get(`/users/patients/list/?page=${page}&page_size=${pageSize}`)
        .then((result) => {
          const { pages_count: pagesCount, page } = result.data;
          dispatch({
            type: GET_PATIENTS,
            payload: result.data,
          });
          return { pagesCount, page };
        })
        .catch((error) => {
          dispatch({
            type: SET_SNACKBAR_ERROR,
            payload:
              error.response?.data?.errors?.[0]?.message ??
              t('There_was_a_problem_retrieving_patients__Please_try_again_'),
          });
          return Promise.reject();
        }),
      'patients-list-area'
    );
  };

export const getPatientsListByQuery =
  (first_name = '', last_name = '', date = 0, month = 0, year = 0, page = 1, pageSize = 7) =>
  async (dispatch) => {
    const params = {
      first_name,
      last_name,
      page,
      page_size: pageSize,
    };
    if (date !== 0) {
      params.date = date;
      params.month = month;
      params.year = year;
    }

    return trackPromise(
      axiosInstance
        .get('/users/patients/list/', {
          params: params,
        })
        .then((result) => {
          const { pages_count: pagesCount, page } = result.data;
          dispatch({
            type: GET_PATIENTS,
            payload: result.data,
          });

          if (result.data?.count === 0) {
            dispatch({
              type: SET_SNACKBAR_INFO,
              payload: t('No_patients_found_'),
            });
          }
          return { pagesCount, page };
        })
        .catch((error) => {
          dispatch({
            type: SET_SNACKBAR_ERROR,
            payload:
              error.response?.data?.errors?.[0]?.message ??
              t('There_was_a_problem_retrieving_patients__Please_try_again_'),
          });
        }),
      'patients-list-area'
    );
  };
// RETRIEVE PATIENT DETAILS WITH ACCOUNT NUMBER

export const getPatientDetails = (account_number) => async (dispatch) => {
  try {
    const result = await axiosInstance.get(`/users/patients/detail/account/${account_number}/`);
    dispatch({
      type: GET_PATIENT_DETAILS,
      payload: { ...result.data, account_number },
    });
    return result.data;
  } catch (error) {
    dispatch({
      type: SET_SNACKBAR_ERROR,
      payload:
        error.response?.data?.errors?.[0]?.message ??
        t('There_was_a_problem_retrieving_patient_details__Please_refresh_the_page_to_try_again_'),
    });
    throw error;
  }
};

//UPDATE PATIENT DETAILS BASED ON ACC NUMBER
export const updatePatientDetails = (patientId, patientDetails, selfUpdated) => async (dispatch) => {
  try {
    if (!_.isEmpty(patientDetails)) {
      const res = await axiosInstance.patch(`/users/patients/detail/${patientId}/`, patientDetails);
      dispatch({
        type: GET_PATIENT_DETAILS,
        payload: { ...res?.data },
      });
    }
    const message = selfUpdated ? t('Successfully_updated_your_details_') : t('Successfully_updated_patient_details_');
    dispatch({
      type: SET_SNACKBAR_SUCCESS,
      payload: message,
    });
  } catch (error) {
    logger.warn(error);
    dispatch({
      type: SET_SNACKBAR_ERROR,
      payload: parseError(
        error.response?.data,
        t('There_is_a_problem_when_updating_patient_details__Please_try_again_later_'),
        t
      ),
    });
    throw error;
  }
};

export const updatePatientEmail = (patientId, email) => async (dispatch) => {
  if (email === undefined) return;
  try {
    await axiosInstance.put(`/users/patients/${patientId}/update/email/`, { email: email });
    dispatch({
      type: GET_PATIENT_DETAILS,
      payload: { email: email },
    });
  } catch (error) {
    logger.warn(error);
  }
};

export const updatePatientPhoneNumber = (patientId, phoneNumber) => async (dispatch) => {
  if (phoneNumber === undefined) return;
  try {
    await axiosInstance.put(`/users/patients/${patientId}/update/phone_number/`, { phone_number: phoneNumber });
    dispatch({
      type: GET_PATIENT_DETAILS,
      payload: { phone_number: phoneNumber },
    });
  } catch (error) {
    logger.warn(error);
  }
};

// GET PATIENT DETAILS WITH ID
export const getPatientDetailsWithId = (id) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(`/users/patients/detail/${id}/`)
      .then((result) => {
        dispatch({
          type: GET_PATIENT_DETAILS,
          payload: { ...result.data },
        });

        resolve(result.data);
      })
      .catch((error) => {
        dispatch({
          type: SET_SNACKBAR_ERROR,
          payload:
            error.response?.data?.errors?.[0]?.message ??
            t('There_was_a_problem_retrieving_patient_details__Please_refresh_the_page_to_try_again_'),
        });
      });
  });
};

// GET ALL EXAMS OF PATIENT - TBD
export const getPatientExams =
  (patient_id = null) =>
  (dispatch) => {
    // If patient_id is not provided, fetch exams for the logged-in patient
    const url = patient_id ? `/eye_screening/list/${patient_id}/` : `/eye_screening/list/`;
    return trackPromise(
      axiosInstance
        .get(url)
        .then((result) => {
          dispatch({
            type: GET_PATIENT_EXAMS,
            payload: result.data,
          });
        })
        .catch((error) => {
          dispatch({
            type: SET_SNACKBAR_ERROR,
            payload:
              error.response?.data?.errors?.[0]?.message ??
              t('There_was_a_problem_when_retrieving_patient_s_exams__Please_refresh_the_page_to_try_again_'),
          });
        }),
      'patient-exams-area'
    );
  };

// GET ALL AUDIO RECORDS OF PATIENT
export const getPatientAudioRecords = (patient_id) => (dispatch) => {
  return trackPromise(
    axiosInstance
      .get(`/audiology/record/list/${patient_id}/`)
      .then((result) => {
        dispatch({
          type: GET_PATIENT_AUDIO_RECORDS,
          payload: result.data,
        });
      })
      .catch((error) => {
        dispatch({
          type: SET_SNACKBAR_ERROR,
          payload: t('There_was_a_problem_retrieving_audio_records__Please_refresh_the_page_to_try_again_'),
        });
      }),
    'patient-exams-area'
  );
};

// GET ALL RISK SERVICES OF PATIENT
export const getPatientRiskServices = (patient_id) => (dispatch) => {
  axiosInstance
    .get(`/services/risk_prediction_service/list/${patient_id}/`)
    .then((result) => {
      dispatch({
        type: GET_PATIENT_RISK_SERVICES,
        payload: result.data,
      });
    })
    .catch((error) => {
      dispatch({
        type: SET_SNACKBAR_ERROR,
        payload:
          error.response?.data?.errors?.[0]?.message ??
          t('There_was_a_problem_when_retrieving_patient_s_exams__Please_refresh_the_page_to_try_again_'),
      });
    });
};

// GET MY DETAILS
export const getMyDetails = (role) => (dispatch) => {
  let api = '';
  switch (role) {
    case PATIENT:
      api = '/users/patients/detail/';
      break;
    default:
      break;
  }

  trackPromise(
    axiosInstance.get(api).then((result) => {
      dispatch({
        type: GET_PATIENT_DETAILS,
        payload: result.data,
      });
    })
  ).catch((error) => {
    dispatch({
      type: SET_SNACKBAR_ERROR,
      payload:
        error.response?.data?.errors?.[0]?.message ??
        t('There_was_a_problem_getting_your_details__Please_try_again_later_'),
    });
  });
};

// CREATE PATIENT
export const createPatient = (patient, history) => async (dispatch) => {
  try {
    const result = await axiosInstance.post('/users/accounts/create/', {
      ...patient,
      signup_application: TELEEYE_MD_WEB,
    });
    dispatch({
      type: CREATE_PATIENT,
      payload: result.data,
    });
    history.push(`/patient/${result.data.account_number}/`);
    dispatch({
      type: SET_SNACKBAR_SUCCESS,
      payload: t('Successfully_created_'),
    });
  } catch (error) {
    dispatch({
      type: SET_SNACKBAR_ERROR,
      payload:
        error.response?.data?.errors?.[0]?.message ??
        t('There_was_a_problem_creating_a_new_patient__Please_try_again_later_'),
    });
  }
};
